/**
 * Recursively filters out null, undefined, and objects/arrays that only contain null/undefined
 */
export function filterNullsAndUndefined(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    const filtered = obj.map((item) => filterNullsAndUndefined(item)).filter((item) => item !== undefined);
    return filtered.length > 0 ? filtered : undefined;
  }

  if (typeof obj === 'object') {
    const filtered: Record<string, unknown> = {};
    let hasValidValue = false;

    for (const [key, value] of Object.entries(obj)) {
      const filteredValue = filterNullsAndUndefined(value);
      if (filteredValue !== undefined) {
        filtered[key] = filteredValue;
        hasValidValue = true;
      }
    }

    return hasValidValue ? filtered : undefined;
  }

  return obj;
}

// Sensitive keys that should be redacted from logs
const SENSITIVE_KEYS = [
  'publicToken',
  'linkToken',
  'accessToken',
  'token',
  'password',
  'secret',
  'apiKey',
  'api_key',
  'authorization',
  'auth',
] as const;

/**
 * Recursively sanitizes an object by redacting sensitive keys
 * Prevents sensitive data (like tokens, passwords) from being logged to error tracking services
 */
export function sanitizeVariables(obj: unknown, depth = 0): unknown {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[Max depth reached]';
  }

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeVariables(item, depth + 1));
  }

  if (typeof obj === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      const isSensitive = SENSITIVE_KEYS.some((sensitiveKey) => lowerKey.includes(sensitiveKey.toLowerCase()));

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeVariables(value, depth + 1);
      }
    }
    return sanitized;
  }

  return obj;
}
